import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, Paper, Radio, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  peopleService, type FamilyDetailResponse, type FamilyListItem, type PossibleDuplicateFamily,
} from '../../services/peopleService';
import { useDebounce } from '../../hooks/useDebounce';
import { familyName } from './familyFormat';
import { grLabel } from '../../utils/personRef';

interface Props {
  open: boolean;
  /** The family the merge was started from. */
  family: FamilyDetailResponse;
  onClose: () => void;
  /** Merge done — `keptFamilyId` is the family that remains. */
  onMerged: (keptFamilyId: string) => void;
}

/** Who is in a family, in one line each — for the side-by-side preview. */
const Members: React.FC<{ title: string; detail: FamilyDetailResponse; kept: boolean }> = ({ title, detail, kept }) => {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{
      p: 2, borderRadius: 2, flex: 1, minWidth: 0,
      borderColor: kept ? 'primary.main' : 'divider',
      bgcolor: kept ? alpha(theme.palette.primary.main, 0.03) : undefined,
    }}>
      <Typography variant="caption" fontWeight={700} color={kept ? 'primary.main' : 'text.secondary'} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {title}
      </Typography>
      <Typography variant="subtitle1" fontWeight={700} noWrap>{familyName(detail.family)}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Children</Typography>
      {detail.people.map(p => <Typography key={p._id} variant="body2">{p.name} <Box component="span" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>· {grLabel(p)}</Box></Typography>)}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Guardians</Typography>
      {detail.guardians.length
        ? detail.guardians.map(g => <Typography key={g._id} variant="body2">{g.name} <Box component="span" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>· {g.relation}</Box></Typography>)
        : <Typography variant="body2" color="text.secondary">None recorded yet</Typography>}
    </Paper>
  );
};

/**
 * Merge two families that are really one household (siblings entered
 * separately). The family that is kept receives the other's children,
 * guardians and admission records; guardians with the same CNIC are kept
 * once; the other family is removed. Runs as one transaction on the server.
 */
const MergeFamiliesDialog: React.FC<Props> = ({ open, family, onClose, onMerged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const thisId = family.family._id;

  // Nothing is pre-selected: merging is permanent, so staff pick on purpose.
  const [otherId, setOtherId] = useState<string | null>(null);
  const [other, setOther] = useState<FamilyDetailResponse | null>(null);
  const [loadingOther, setLoadingOther] = useState(false);
  const [keep, setKeep] = useState<'this' | 'other'>('this');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [results, setResults] = useState<FamilyListItem[]>([]);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    peopleService.listFamilies({ q: search.trim(), limit: 8 })
      .then(r => setResults(r.items.filter(f => f._id !== thisId)))
      .catch((err: any) => enqueueSnackbar(err?.message || 'Search failed', { variant: 'error' }));
  }, [search, thisId, enqueueSnackbar]);

  useEffect(() => {
    if (!otherId) { setOther(null); return; }
    setLoadingOther(true);
    peopleService.getFamily(otherId)
      .then(d => {
        setOther(d);
        // Keep the linked family by default — it carries the confirmed contacts.
        setKeep(family.family.status !== 'resolved' && d.family.status === 'resolved' ? 'other' : 'this');
      })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load that family', { variant: 'error' }))
      .finally(() => setLoadingOther(false));
  }, [otherId, family.family.status, enqueueSnackbar]);

  const kept = keep === 'this' ? family : other;
  const merged = keep === 'this' ? other : family;

  const merge = async () => {
    if (!kept || !merged) return;
    setMerging(true);
    try {
      await peopleService.mergeFamilies({
        sourceFamilyId: merged.family._id,
        targetFamilyId: kept.family._id,
        keepGuardians: 'both_dedupe_by_cnic',
      });
      enqueueSnackbar(`Merged into ${familyName(kept.family)}`, { variant: 'success' });
      onMerged(kept.family._id);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not merge the families', { variant: 'error' });
    } finally {
      setMerging(false);
    }
  };

  const pick = (s: PossibleDuplicateFamily | FamilyListItem, reasons?: string[]) => {
    const id = 'familyId' in s ? s.familyId : s._id;
    const children = s.children.map(c => c.name).join(', ');
    return (
      <Paper
        key={id}
        variant="outlined"
        onClick={() => setOtherId(id)}
        sx={{ p: 1.25, borderRadius: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, borderColor: otherId === id ? 'primary.main' : 'divider' }}
      >
        <Radio size="small" checked={otherId === id} sx={{ p: 0.5 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{s.displayName || 'Unnamed family'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {children || 'No children'}{reasons?.length ? `  ·  ${reasons.join(', ')}` : ''}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={merging ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Merge with another family</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Which family is the same household?</Typography>
            {family.possibleDuplicates.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Likely matches</Typography>
                {family.possibleDuplicates.map(m => pick(m, m.reasons))}
              </Stack>
            )}
            <TextField
              size="small"
              fullWidth
              placeholder="Or search by family, guardian, phone, child or GR…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            {results.length > 0 && <Stack spacing={1} sx={{ mt: 1 }}>{results.map(r => pick(r))}</Stack>}
          </Box>

          {loadingOther && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
          {other && !loadingOther && (
            <>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Which family should remain?</Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant={keep === 'this' ? 'contained' : 'outlined'} disableElevation size="small" onClick={() => setKeep('this')} sx={{ textTransform: 'none' }}>
                    Keep {familyName(family.family)}
                  </Button>
                  <Button variant={keep === 'other' ? 'contained' : 'outlined'} disableElevation size="small" onClick={() => setKeep('other')} sx={{ textTransform: 'none' }}>
                    Keep {familyName(other.family)}
                  </Button>
                </Stack>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Members title="Remains" detail={kept!} kept />
                <Members title="Merged in, then removed" detail={merged!} kept={false} />
              </Stack>
              <Alert severity="info">
                {familyName(merged!.family)}&apos;s children, guardians and admission records move to {familyName(kept!.family)}.
                A guardian recorded in both families (same CNIC) is kept once. This can&apos;t be undone from here.
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={merging} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disableElevation color="warning" onClick={merge} disabled={!other || merging} sx={{ textTransform: 'none' }}>
          {merging ? 'Merging…' : 'Merge families'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeFamiliesDialog;
